import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Dialog,
  Inline,
  Notice,
  Stack,
  Text,
} from 'braid-design-system';
import { useCallback, useRef, useState } from 'react';

import { useAppConfig } from 'src/modules/appConfig';

import translations from '../AvatarContainer/.vocab';

import { AddPhotoFlow } from './AddPhotoFlow';
import type { ValidationError, SystemError } from './validateImage';

export type AddAvatarStep = 'Crop' | 'UploadPreview' | 'Uploading';

interface Props {
  currentStep: AddAvatarStep;
  setCurrentStep: (step: AddAvatarStep) => void;
  showAddPhotoDialog: boolean;
  onClose: () => void;
  croppedImage: File | null;
  setCroppedImage: (file: File | null) => void;
  openDialog: () => void;
  setValidationError: (value: ValidationError | SystemError | false) => void;
  originalFile: File | null;
  setOriginalFile: (file: File | null) => void;
}
export const AddPhotoDialog = ({
  currentStep,
  setCurrentStep,
  showAddPhotoDialog,
  onClose,
  croppedImage,
  setCroppedImage,
  openDialog,
  setValidationError,
  originalFile,
  setOriginalFile,
}: Props) => {
  const hubble = useHubble();
  const { t } = useTranslations(translations);
  const [showDiscardChangesDialog, setShowDiscardChangesDialog] =
    useState(false);
  const [showInProgressMessage, setShowInProgressMessage] = useState(false);
  const { zone, language } = useAppConfig();
  const hasAnyError = useRef(false);

  const handleDialogClose = () => {
    // If we're on the UploadPreview step and have a selected file, show confirmation
    if (currentStep === 'UploadPreview' && croppedImage) {
      setShowDiscardChangesDialog(true);
      return false;
    }
    if (currentStep === 'Uploading') {
      if (hasAnyError.current) {
        // Allow dialog to close when there's an error
        setShowDiscardChangesDialog(false);
        onClose();
        return;
      }
      setShowInProgressMessage(true);
      return false;
    }
    setShowDiscardChangesDialog(false);
    onClose();
  };

  const handleConfirmDiscardChanges = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'button',
        name: 'discard',
      },
      displayLanguage: language,
      zone,
    });

    setShowDiscardChangesDialog(false);
    onClose();
  };

  const handleCancelDiscardChanges = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'button',
        name: 'cancel discard',
      },
      displayLanguage: language,
      zone,
    });

    setShowDiscardChangesDialog(false);
  };

  // Callback to clear uploading message
  const handleUploadStepEnd = useCallback(
    () => setShowInProgressMessage(false),
    [],
  );

  return (
    <>
      <Dialog
        open={showAddPhotoDialog}
        onClose={handleDialogClose}
        title={t('Profile photo')}
        width={currentStep === 'Crop' ? 'small' : 'xsmall'}
        id="add-photo-dialog"
      >
        {showInProgressMessage && (
          <Notice tone="info">
            <Text>{t('Upload in progress')}</Text>
          </Notice>
        )}
        <AddPhotoFlow
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          closeDialog={onClose}
          croppedImage={croppedImage}
          setCroppedImage={setCroppedImage}
          openDialog={openDialog}
          setValidationError={setValidationError}
          originalFile={originalFile}
          setOriginalFile={setOriginalFile}
          onUploadStepEnd={handleUploadStepEnd}
          hasError={hasAnyError}
        />
      </Dialog>
      <Dialog
        open={showDiscardChangesDialog}
        onClose={handleCancelDiscardChanges}
        title={t('Discard changes')}
        width="xsmall"
        id="discard-changes-dialog"
      >
        <Stack space="medium">
          <Text>{t('Are you sure you want to leave without saving?')}</Text>
          <Box display="flex" justifyContent="flexEnd">
            <Inline space="small">
              <Button
                tone="critical"
                variant="ghost"
                onClick={handleConfirmDiscardChanges}
              >
                {t('Discard')}
              </Button>
              <Button
                variant="transparent"
                onClick={handleCancelDiscardChanges}
              >
                {t('Cancel')}
              </Button>
            </Inline>
          </Box>
        </Stack>
      </Dialog>
    </>
  );
};
